import type { BoxInstanceId, InstanceId, RoomId } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import {
  DEFAULT_SETTINGS,
  EMPTY_STUDIO,
  SAVE_SCHEMA_VERSION,
  type GameSave,
  type RoomState,
} from '@/core/persistence/schema/v1';
import { dayKeyOf } from './time';

export interface NewSaveParams {
  saveId: string;
  rngSeed: number;
  nowWallMs: number;
  locale?: GameSave['settings']['locale'];
}

/** A brand-new game (content-schemas §10.3): starter boxes in Home, Tiệm fixtures pre-built, defaults owned. */
export function createNewSave(content: ContentRegistry, p: NewSaveParams): GameSave {
  const today = dayKeyOf(p.nowWallMs);
  const defaults = [...content.wallFloors.values()].filter((w) => w.isDefault).map((w) => w.id);
  const room = (roomId: RoomId): RoomState => {
    const layout = content.rooms.get(roomId);
    if (!layout) throw new Error(`Missing room layout ${roomId}`);
    return {
      roomId,
      wallId: layout.defaultWall,
      floorId: layout.defaultFloor,
      expansions: [],
      placements: {},
      slots: {},
    };
  };

  const save: GameSave = {
    schemaVersion: SAVE_SCHEMA_VERSION,
    saveId: p.saveId,
    revision: 0,
    createdAt: p.nowWallMs,
    updatedAt: p.nowWallMs,
    contentVersion: content.version,
    rngSeed: p.rngSeed >>> 0,
    clock: { playTimeMs: 0, firstDayKey: today, lastSeenDayKey: today },
    wallet: { coins: content.balance.startCoins },
    stars: { total: 0, sources: {} },
    inventory: {
      instances: {},
      productStacks: {},
      boxes: {},
      nextInstanceSeq: 1,
      favorites: [],
      orphans: [],
    },
    rooms: { home: room('home'), minimart: room('minimart'), studio: room('studio') },
    ownedWallFloors: defaults,
    catalogSeen: [],
    shop: {
      isOpen: false,
      perks: [],
      sim: {
        nextCustomerAtPlayMs: null,
        rngState: (p.rngSeed ^ 0x9e3779b9) >>> 0,
        customerSeq: 0,
        active: [],
      },
      ledgerSummary: {
        revenueTotal: 0,
        unitsSoldTotal: 0,
        salesCount: 0,
        stockSpentTotal: 0,
        catalogSpentTotal: 0,
        rewardsCoinsTotal: 0,
        byProduct: {},
      },
    },
    orders: {
      open: [],
      nextOrderAtPlayMs: null,
      firstOrderDone: {},
      orderSeq: 0,
      rngState: (p.rngSeed ^ 0x85ebca6b) >>> 0,
    },
    coziness: {
      home: { current: 0, best: 0, bestLevel: 0 },
      minimart: { current: 0, best: 0, bestLevel: 0 },
      studio: { current: 0, best: 0, bestLevel: 0 },
    },
    studio: { ...EMPTY_STUDIO, rngState: (p.rngSeed ^ 0xc2b2ae35) >>> 0 },
    npcs: {},
    daily: { dayKey: today, perNpc: {} },
    mailbox: {
      lastGrantedDayKey: null,
      grantsCount: 0,
      postcardCursor: 0,
      letterCursor: 0,
      stickerCursor: 0,
      miniBoxCursor: 0,
      pending: [],
      special: [],
      claimedCount: 0,
    },
    miniGames: {},
    story: { unlockedChapters: ['ch0'], seenChapters: [], postcards: [], letters: [], neighborhood: {} },
    album: { photoIds: [] },
    avatar: { hair: 'av_hair_1', shirtColor: 'av_shirt_cream', hat: 'av_hat_bucket_pink', frame: null },
    cosmetics: {
      titles: [],
      badges: [],
      stickers: [],
      photoFrames: [],
      avatarParts: [
        'av_hair_1',
        'av_hair_2',
        'av_hair_3',
        'av_shirt_cream',
        'av_shirt_pink',
        'av_shirt_mint',
        'av_shirt_lavender',
        'av_hat_bucket_pink',
      ],
      avatarFrames: [],
      equippedTitle: null,
    },
    achievements: {},
    stats: { counters: {}, distinct: {} },
    suggestions: {
      tasks: {},
      optionalActive: [],
      repeatable: { active: [], seq: 0, lastClaimAtPlayMs: null, rngState: (p.rngSeed ^ 0xc2b2ae35) >>> 0 },
    },
    // The unlock evaluator grants unl_home (no prerequisites) on the first bootSession.
    unlockedIds: [],
    features: [],
    scenes: ['home'],
    flags: {},
    dialogue: { appliedEffectKeys: [], seenDialogueIds: [] },
    rewardKeys: [],
    txLog: { recent: [] },
    lastSession: { sceneId: 'home', cameras: {}, panel: 'none', savedAtWallMs: p.nowWallMs },
    settings: { ...DEFAULT_SETTINGS, locale: p.locale ?? 'vi' },
  };

  // Starter boxes sit on Home's floor until opened.
  const home = content.rooms.get('home');
  (home?.boxSpawns ?? []).forEach((spawn, i) => {
    const id: BoxInstanceId = `bx_${String(i + 1)}`;
    save.inventory.boxes[id] = {
      boxInstanceId: id,
      boxDefId: spawn.boxDefId,
      roomId: 'home',
      x: spawn.x,
      y: spawn.y,
      opened: false,
    };
  });

  // Tiệm fixtures exist from day one so opening the shop later is not a special case.
  const shop = content.rooms.get('minimart');
  for (const f of shop?.fixtures ?? []) {
    const instanceId: InstanceId = `i_${save.inventory.nextInstanceSeq.toString(36)}`;
    save.inventory.nextInstanceSeq += 1;
    save.inventory.instances[instanceId] = {
      instanceId,
      itemId: f.itemId,
      acquiredBy: 'preplaced',
      acquiredAtPlayMs: 0,
      location: { kind: 'placed', roomId: 'minimart' },
    };
    save.rooms.minimart.placements[instanceId] = {
      instanceId,
      layer: 'object',
      x: f.x,
      y: f.y,
      rotation: f.rotation,
    };
  }
  return save;
}
